/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as SelectTTSIcon } from 'assets/icons/select-tts.svg'
import { ReactComponent as PlayTTSIcon } from 'assets/icons/play-tts.svg'
import { ReactComponent as PauseTTSIcon } from 'assets/icons/pause-tts.svg'
import { ReactComponent as SettingsTTSIcon } from 'assets/icons/settings-tts.svg'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import {
  AssignmentBodyItem,
  QuestionBodyItem,
  TTSKey
} from 'interfaces/assignments'

const TTS_ID_PREFIX = 'tts-'

/**
 * Builds the canonical DOM id format used by assignment TTS spans.
 *
 * Backend sends `tts_keys` metadata (source_row_id, source_type, paragraph_index, audio_url),
 * while authored HTML includes matching ids in this exact format. We use this helper to map
 * metadata to concrete DOM element ids for click playback and sequential queue playback.
 */
const getTtsDomId = (ttsKey: TTSKey) => {
  return `${TTS_ID_PREFIX}${ttsKey.source_row_id}-${ttsKey.source_type}-p${ttsKey.paragraph_index}`
}

/**
 * Student benchmark responses supply audio metadata without teacher preview's
 * paragraph-level `tts-*` ids. Map that metadata to the existing assessment
 * areas so each supported passage, direction, question, or choice is still
 * independently selectable and highlighted.
 */
const getAssessmentTtsDomId = (ttsKey: TTSKey) => {
  const assessmentDomIds: Record<string, string> = {
    passage_directions: 'passage_directions',
    passage_1: 'passage_1',
    passage_2: 'passage_2',
    stimulus_tab_one: 'passage_1',
    stimulus_tab_two: 'passage_2',
    question_text: 'question'
  }

  return assessmentDomIds[ttsKey.source_type] || ttsKey.source_type
}

/**
 * Prefer the same paragraph-level markup used in teacher preview. The student
 * assessment container is only used when that backend markup is absent.
 */
const getTtsElement = (ttsKey: TTSKey) => {
  return (
    document.getElementById(getTtsDomId(ttsKey)) ||
    document.getElementById(getAssessmentTtsDomId(ttsKey))
  )
}

const TextToSpeechContainer = styled.div`
  display: flex;
  width: 280px;
  gap: 9px;
  align-items: center;
  justify-content: center;
`

const TextToSpeechText = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  align-self: flex-start;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

/**
 * Active TTS controls preserve the icon's gray background while recoloring only
 * the foreground artwork with the student portal's primary text token.
 */
const TTSIconButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  ${(props) =>
    props.$active &&
    css`
      svg path:not(:first-child) {
        fill: #000000;
      }
    `}

  &:disabled {
    cursor: not-allowed;
  }
`

/**
 * Keep the speech-selection control aligned with the other TTS controls.
 */
const SpeechSelectButton = styled(TTSIconButton)`
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  border-radius: 50%;
`

/**
 * Browser media APIs surface benign aborts when a previous fetch/play attempt is
 * interrupted by a new one. We explicitly ignore those cases so only actionable
 * playback failures affect UI state.
 */
const isBenignMediaAbort = (error?: DOMException | MediaError | null) => {
  if (!error) {
    return false
  }

  if ('code' in error && error.code === 1) {
    return true
  }

  const errorName = 'name' in error ? error.name : ''
  const errorMessage = 'message' in error ? error.message : ''

  return (
    errorName === 'AbortError' ||
    errorMessage.includes('NS_BINDING_ABORTED') ||
    errorMessage.includes('media resource was aborted')
  )
}

const SpeechControlsV2 = () => {
  /**
   * Detect whether the current screen is assignment-based.
   */
  const { assessmentType } = useParams()
  const isAssignmentPreview = assessmentType === 'assignments'

  /**
   * Current assignment page is required to resolve stimulus/body `tts_keys`.
   */
  const { currentPage } = useSelector(SelectAssignmentState)

  /**
   * UI state for settings and controls.
   */
  const [showSettings, setShowSettings] = useState(false)
  const [audioSpeed, setAudioSpeed] = useState('normal')
  const [volume, setVolume] = useState(0.5)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [filterActive, setFilterActive] = useState(false)
  /**
   * Records only the playback button directly clicked by the student. Playback
   * completion and internal stops stay neutral instead of changing selection.
   */
  const [selectedPlaybackControl, setSelectedPlaybackControl] = useState<
    'play' | 'pause' | null
  >(null)

  /**
   * Pre-generated TTS state is kept local so file-based playback does not rely on
   * QuestionnaireContext's synthesized-speech transitions.
   */
  const [preGeneratedSpeechEnabled, setPreGeneratedSpeechEnabled] =
    useState(false)
  const [preGeneratedKeyToSpeak, setPreGeneratedKeyToSpeak] = useState<
    string | null
  >(null)
  /**
   * Remembers the last key/url pair we started so repeated effects or click events do not
   * re-trigger the same network/media request while that exact audio is already playing.
   */
  const lastStartedAudioRef = useRef<{
    key: string | null
    url: string | null
  }>({
    key: null,
    url: null
  })

  const { currentQuestion, questions } = useContext(QuestionnaireContext)
  /**
   * SpeechControlsV2 owns its own audio element so this pre-generated flow stays fully local
   * and does not depend on legacy audio refs from QuestionnaireContext.
   */
  const audioRef = useRef<HTMLAudioElement | null>(null)

  /**
   * Shared child questions render the source question's passage, so passage audio
   * must come from that source while question and choice audio stays child-specific.
   */
  const assessmentTtsKeys = useMemo(() => {
    const currentTtsKeys = currentQuestion?.tts_keys || []

    if (!currentQuestion?.shares_passage_with) {
      return currentTtsKeys
    }

    const sharedQuestion = questions.find(
      (question) => question.id === currentQuestion?.shares_passage_with
    )

    if (!sharedQuestion) {
      return currentTtsKeys
    }

    const stimulusSourceTypes = new Set([
      'passage_directions',
      'passage_1',
      'passage_2',
      'stimulus_tab_one',
      'stimulus_tab_two'
    ])

    return [
      ...(sharedQuestion.tts_keys || []).filter((ttsKey) =>
        stimulusSourceTypes.has(ttsKey.source_type)
      ),
      ...currentTtsKeys.filter(
        (ttsKey) => !stimulusSourceTypes.has(ttsKey.source_type)
      )
    ]
  }, [currentQuestion, questions])
  const hasAssessmentPreGeneratedTts = assessmentTtsKeys.length > 0
  /**
   * These two values drive all UI and playback behavior:
   * - whether "select TTS" mode is currently enabled
   * - which exact tts-* DOM id should be highlighted/played
   */
  const activeSpeechEnabled = preGeneratedSpeechEnabled
  const activeKeyToSpeak = preGeneratedKeyToSpeak

  /**
   * Fast lookup table from TTS DOM id -> pre-generated audio_url.
   *
   * Sources merged:
   * 1) assignment stimulus/body tts_keys
   * 2) assessment currentQuestion tts_keys
   */
  const preGeneratedAudioByTtsKey = useMemo(() => {
    const audioMap: Record<string, string> = {}

    const addTtsKeyAudio = (ttsKey?: TTSKey) => {
      if (!ttsKey?.audio_url) {
        return
      }

      audioMap[getTtsDomId(ttsKey)] = ttsKey.audio_url
    }

    if (isAssignmentPreview && currentPage) {
      currentPage.stimulus_tts_keys?.forEach(addTtsKeyAudio)
      currentPage.body_items?.forEach(
        (bodyItem: AssignmentBodyItem | QuestionBodyItem) => {
          bodyItem.tts_keys?.forEach(addTtsKeyAudio)
        }
      )
    }

    if (hasAssessmentPreGeneratedTts) {
      assessmentTtsKeys.forEach(addTtsKeyAudio)
    }

    return audioMap
  }, [
    assessmentTtsKeys,
    currentPage,
    hasAssessmentPreGeneratedTts,
    isAssignmentPreview
  ])

  /**
   * Fallback assessment containers resolve to their canonical TTS metadata
   * key, preserving the pre-generated audio URL and active state per area.
   */
  const assessmentTtsKeyByElementId = useMemo(() => {
    const ttsKeyByElementId: Record<string, string> = {}

    if (isAssignmentPreview) {
      return ttsKeyByElementId
    }

    assessmentTtsKeys.forEach((ttsKey) => {
      const canonicalTtsId = getTtsDomId(ttsKey)
      ttsKeyByElementId[canonicalTtsId] = canonicalTtsId
      ttsKeyByElementId[getAssessmentTtsDomId(ttsKey)] = canonicalTtsId
    })

    return ttsKeyByElementId
  }, [assessmentTtsKeys, isAssignmentPreview])

  /**
   * Deterministic assignment queue in backend/content order.
   *
   * Playback order:
   * 1) stimulus keys
   * 2) body item keys by order_index
   *
   * Only keys with valid audio_url are included.
   */
  const orderedPreGeneratedSpeechQueue = useMemo(() => {
    if (isAssignmentPreview && !currentPage) {
      return [] as string[]
    }

    const queue: string[] = []

    const appendTtsKeys = (ttsKeys: TTSKey[] = []) => {
      ttsKeys.forEach((ttsKey) => {
        const ttsId = getTtsDomId(ttsKey)

        if (ttsKey.audio_url && preGeneratedAudioByTtsKey[ttsId]) {
          queue.push(ttsId)
        }
      })
    }

    if (isAssignmentPreview && currentPage) {
      appendTtsKeys(currentPage.stimulus_tts_keys || [])

      const sortedBodyItems = [...(currentPage.body_items || [])].sort(
        (a, b) => a.order_index - b.order_index
      )

      sortedBodyItems.forEach((bodyItem) => {
        appendTtsKeys(bodyItem.tts_keys || [])
      })
    } else if (hasAssessmentPreGeneratedTts) {
      appendTtsKeys(assessmentTtsKeys)
    }

    return queue
  }, [
    assessmentTtsKeys,
    currentPage,
    hasAssessmentPreGeneratedTts,
    isAssignmentPreview,
    preGeneratedAudioByTtsKey
  ])

  /**
   * Only TTS nodes rendered by the currently visible stimulus tab belong in the
   * sequential queue; hidden tab content must not be read or reveal that tab.
   */
  const getVisiblePreGeneratedSpeechQueue = () => {
    return orderedPreGeneratedSpeechQueue.filter((ttsId) => {
      if (document.getElementById(ttsId)) {
        return true
      }

      const assessmentTtsKey = assessmentTtsKeys.find(
        (ttsKey) => getTtsDomId(ttsKey) === ttsId
      )

      if (!assessmentTtsKey) {
        return false
      }

      return !!getTtsElement(assessmentTtsKey)
    })
  }

  /**
   * Playback rate mapping used by both synthesized and file-based audio playback.
   */
  const getRate = () => {
    switch (audioSpeed) {
      case 'slow':
        return 0.9
      case 'normal':
        return 1
      case 'fast':
        return 1.2
      default:
        return 1
    }
  }

  /**
   * Advances playback to the next key in the pre-generated queue.
   */
  const nextKeyToSpeak = () => {
    const visibleQueue = getVisiblePreGeneratedSpeechQueue()
    const currentKeyIndex = visibleQueue.findIndex(
      (key) => key === activeKeyToSpeak
    )

    /**
     * If playback was started manually from a clicked span that is not found in queue order,
     * restart from the first known queue item rather than leaving playback stalled.
     */
    if (currentKeyIndex === -1) {
      setPreGeneratedKeyToSpeak(visibleQueue[0] || null)
      return
    }

    const nextKey = visibleQueue[currentKeyIndex + 1] || null

    /**
     * The play control remains selected while the queue is progressing, but it
     * returns to its neutral state once the final page item has finished.
     */
    if (!nextKey) {
      setSelectedPlaybackControl(null)
    }

    setPreGeneratedKeyToSpeak(nextKey)
  }

  /**
   * Stops current playback and resets active key.
   */
  const onStop = () => {
    audioRef.current?.pause()
    setIsAudioPlaying(false)
    lastStartedAudioRef.current = {
      key: null,
      url: null
    }
    setPreGeneratedKeyToSpeak(null)
  }

  /**
   * Pause uses the existing stop behavior while retaining the clicked Pause
   * button's visual selection state.
   */
  const onPause = () => {
    setSelectedPlaybackControl('pause')
    onStop()
  }

  /**
   * Plays backend-provided audio_url directly.
   */
  const playAudioFromUrl = (audioUrl: string) => {
    const audioElement = audioRef.current

    if (!audioUrl || !audioElement) {
      return
    }

    const isDuplicatePlayRequest =
      lastStartedAudioRef.current.key === activeKeyToSpeak &&
      lastStartedAudioRef.current.url === audioUrl &&
      !audioElement.paused &&
      !audioElement.ended

    if (isDuplicatePlayRequest) {
      return
    }

    /**
     * Firefox can emit aborted-media errors when source changes or range requests restart.
     * Those should not flip the UI into an error state because playback commonly continues.
     */
    audioElement.onerror = () => {
      const mediaError = audioElement.error

      if (isBenignMediaAbort(mediaError)) {
        return
      }

      setIsAudioPlaying(false)
    }

    const normalizedCurrentSource = audioElement.currentSrc || audioElement.src
    const shouldUpdateSource = normalizedCurrentSource !== audioUrl

    /**
     * Update the audio element source only when the URL actually changes.
     * Avoiding redundant source resets prevents unnecessary media abort/reload cycles.
     */
    if (shouldUpdateSource) {
      audioElement.src = audioUrl
    }

    lastStartedAudioRef.current = {
      key: activeKeyToSpeak,
      url: audioUrl
    }
    audioElement.playbackRate = getRate()
    const playPromise = audioElement.play()

    /**
     * `play()` is promise-based in modern browsers; treat benign aborts as expected and
     * only clear playback state for real failures.
     */
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error: DOMException) => {
        if (isBenignMediaAbort(error)) {
          return
        }

        setIsAudioPlaying(false)
      })
    }

    setIsAudioPlaying(true)
    audioElement.onended = () => {
      setIsAudioPlaying(false)
      lastStartedAudioRef.current = {
        key: null,
        url: null
      }
      nextKeyToSpeak()
    }
  }

  /**
   * Main playback dispatcher for pre-generated audio.
   */
  React.useEffect(() => {
    if (!activeKeyToSpeak) return

    const audioUrl = preGeneratedAudioByTtsKey[activeKeyToSpeak]

    /**
     * Playback is keyed entirely by DOM id. When the currently active key has a mapped URL,
     * start it; otherwise skip forward to the next queued item.
     */
    if (audioUrl) {
      playAudioFromUrl(audioUrl)
      return
    }

    nextKeyToSpeak()
  }, [activeKeyToSpeak, preGeneratedAudioByTtsKey])

  /**
   * Pre-generated TTS visual state synchronization.
   * Applies/removes ondemandtts and playtts directly on matching tts DOM nodes.
   */
  React.useEffect(() => {
    const ttsElements = isAssignmentPreview
      ? (Array.from(
          document.querySelectorAll(`[id^="${TTS_ID_PREFIX}"]`)
        ) as HTMLElement[])
      : assessmentTtsKeys
          .map(getTtsElement)
          .filter((element): element is HTMLElement => !!element)

    /**
     * Rebuild the visual state on every relevant change so DOM markup stays authoritative:
     * - all tts-* nodes lose previous classes first
     * - speech mode adds selectable styling
     * - current key adds active playback styling
     */
    ttsElements.forEach((element) => {
      element.classList.remove('ondemandtts')
      element.classList.remove('playtts')

      if (activeSpeechEnabled) {
        element.classList.add('ondemandtts')
      }

      const canonicalTtsId = isAssignmentPreview
        ? element.id
        : assessmentTtsKeyByElementId[element.id]

      if (activeKeyToSpeak && canonicalTtsId === activeKeyToSpeak) {
        element.classList.add('playtts')
      }
    })
  }, [
    activeKeyToSpeak,
    activeSpeechEnabled,
    assessmentTtsKeyByElementId,
    assessmentTtsKeys,
    isAssignmentPreview
  ])

  /**
   * Keep the currently playing TTS node in view while sequential playback advances.
   */
  React.useEffect(() => {
    if (!activeKeyToSpeak) {
      return
    }

    /**
     * Sequential playback may move through content that is off-screen, so we keep the active
     * DOM target centered for visibility and accessibility.
     */
    const activeTtsKey = assessmentTtsKeys.find(
      (ttsKey) => getTtsDomId(ttsKey) === activeKeyToSpeak
    )
    const activeElement = activeTtsKey
      ? getTtsElement(activeTtsKey)
      : document.getElementById(activeKeyToSpeak)
    activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [activeKeyToSpeak, assessmentTtsKeys])

  /**
   * Pre-generated TTS click capture while speech mode is enabled.
   *
   * We listen in capture phase so clicks on nested HTML structures are still resolved
   * to the nearest tts-* node and converted into a local playback key.
   */
  React.useEffect(() => {
    if (!preGeneratedSpeechEnabled) {
      return
    }

    const onDocumentClick = (event: Event) => {
      const clickedElement = event.target as HTMLElement | null
      let ttsNode = clickedElement?.closest?.(
        `[id^="${TTS_ID_PREFIX}"]`
      ) as HTMLElement | null

      /**
       * Find the registered benchmark area instead of stopping at unrelated
       * nested ids inside rich text such as tables, images, or equations.
       */
      if (!isAssignmentPreview) {
        let currentElement = clickedElement

        while (
          currentElement &&
          !assessmentTtsKeyByElementId[currentElement.id]
        ) {
          currentElement = currentElement.parentElement
        }

        ttsNode = currentElement
      }

      /**
       * Only tts-* nodes backed by a mapped URL should start playback.
       * Everything else should behave normally and continue bubbling.
       */
      if (!ttsNode?.id) {
        return
      }

      const canonicalTtsId = isAssignmentPreview
        ? ttsNode.id
        : assessmentTtsKeyByElementId[ttsNode.id]

      if (!canonicalTtsId || !preGeneratedAudioByTtsKey[canonicalTtsId]) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      setPreGeneratedKeyToSpeak(canonicalTtsId)
    }

    document.addEventListener('click', onDocumentClick, true)

    return () => {
      document.removeEventListener('click', onDocumentClick, true)
    }
  }, [
    assessmentTtsKeyByElementId,
    isAssignmentPreview,
    preGeneratedAudioByTtsKey,
    preGeneratedSpeechEnabled
  ])

  /**
   * Keeps existing audio playback speed in sync with settings.
   */
  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = getRate()
    }
  }, [audioSpeed])

  /**
   * Keeps existing audio volume in sync with settings.
   */
  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  /**
   * Toggles speech selection mode.
   */
  const onSpeechHandler = () => {
    const nextEnabledState = !preGeneratedSpeechEnabled
    setPreGeneratedSpeechEnabled(nextEnabledState)

    /**
     * Turning selection mode off should also stop playback and clear highlight state
     * so the UI returns to a neutral reading mode immediately.
     */
    if (!nextEnabledState) {
      onStop()
    }
  }

  /**
   * Toggles the settings dropdown panel.
   */
  const onFilterHandler = () => {
    setShowSettings(!showSettings)
    setFilterActive(!filterActive)
  }

  /**
   * Starts playback from first available item in the active queue.
   */
  const onPlay = () => {
    setSelectedPlaybackControl('play')
    const queue = getVisiblePreGeneratedSpeechQueue()
    if (!queue.length) return

    /**
     * Play always starts from the first backend-ordered key so repeated plays are deterministic
     * regardless of which node was clicked previously.
     */
    setPreGeneratedKeyToSpeak(queue[0])
  }

  return (
    <TextToSpeechContainer>
      <TextToSpeechText>Text to Speech</TextToSpeechText>
      <SpeechSelectButton
        $active={preGeneratedSpeechEnabled}
        type='button'
        onClick={onSpeechHandler}
        aria-label='Select Text to Speech voice'
      >
        <SelectTTSIcon />
      </SpeechSelectButton>

      <TTSIconButton
        type='button'
        $active={selectedPlaybackControl === 'play'}
        onClick={onPlay}
        disabled={isAudioPlaying}
        aria-label='Play Text to Speech'
      >
        <PlayTTSIcon />
      </TTSIconButton>

      <TTSIconButton
        type='button'
        $active={selectedPlaybackControl === 'pause'}
        onClick={onPause}
        aria-label='Pause Text to Speech'
        disabled={!isAudioPlaying}
      >
        <PauseTTSIcon />
      </TTSIconButton>

      <TTSIconButton
        type='button'
        $active={filterActive}
        onClick={onFilterHandler}
        aria-label='Text to Speech settings'
      >
        <SettingsTTSIcon />
      </TTSIconButton>

      <audio
        hidden
        ref={audioRef}
        key='audioSrc'
        id='audioPlayback'
        controls
        preload='none'
      />

      <div className={`settings-panel ${showSettings && 'volumeEnabled'}`}>
        <div className='volume-section'>
          <p>Volume:</p>
          <div className='volume-range'>
            <p>{(volume * 100).toFixed(0)}%</p>
            <input
              type='range'
              onChange={(event) => setVolume(+event.target.value)}
              min={0}
              max={1}
              step={0.01}
              value={volume}
            />
          </div>
        </div>
        <div className='speed-section'>
          <p>Speed:</p>
          <div className='speed-buttons'>
            <button
              type='button'
              onClick={() => setAudioSpeed('slow')}
              className={audioSpeed === 'slow' ? 'active' : ''}
            >
              Slow
            </button>
            <button
              type='button'
              onClick={() => setAudioSpeed('normal')}
              className={audioSpeed === 'normal' ? 'active' : ''}
            >
              Normal
            </button>
            <button
              type='button'
              onClick={() => setAudioSpeed('fast')}
              className={audioSpeed === 'fast' ? 'active' : ''}
            >
              Fast
            </button>
          </div>
        </div>
      </div>
    </TextToSpeechContainer>
  )
}

export default SpeechControlsV2
